import { CircularProgress } from '@mui/material'
import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinSettingMetadata,
  useDigitalTwinSettings,
} from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting, SettingSource } from 'api/digitalTwin/digitalTwin.types'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Inline, LoadingPlaceholderContainer, Switch } from 'ui/atoms'
import BarCalendar from 'ui/components/BarCalendar/BarCalendar'
import type { Event as CalendarBarEvent } from 'ui/components/BarCalendar/BarCalendar.types'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import TimePeriodPicker from 'ui/molecules/TimePeriodPicker/TimePeriodPicker'
import Datetime, { Month } from 'utils/datetime/datetime'
import { DEFAULT_SOURCES, filterBySource } from '../../useSourceFilter'
import { generateDTSCCategoriesAndEvents, isBarOfSetting } from './DigitalTwinSettingsCalendar.helper'
import styles from './DigitalTwinSettingsCalendar.module.less'

type DigitalTwinSettingsCalendarProps = {
  digitalTwin: DigitalTwin
  onEditDeviationSetting: (setting: DigitalTwinSetting | undefined) => void
  /** Rings the setting bars it returns true for. */
  isEventHighlighted?: (event: CalendarBarEvent) => boolean
  onPeriodChange?: (startTime: ISODateTime, endTime: ISODateTime) => void
  configurableProperties: ConfigurableProperty[]
  filteredProperties: ConfigurableProperty[]
  settingPriorityLevels: SystemSettingPriorityLevel[] | null | undefined
  activeBaseSettings: DigitalTwinSettingMetadata[]
  settingPropertyFilterEnabled: boolean
  refetchTriggerToggle: boolean
  isCollectedSettings?: boolean
  currentOptimizationPriorityLevels?: number[]
  selectedSources?: SettingSource[]
  sourceFilterEnabled?: boolean
}

type CalendarZoom = `productionPlan` | `week` | `month` | `custom`

export default function DigitalTwinSettingsCalendar({
  digitalTwin,
  onEditDeviationSetting,
  isEventHighlighted,
  onPeriodChange,
  configurableProperties,
  filteredProperties,
  settingPriorityLevels,
  activeBaseSettings,
  settingPropertyFilterEnabled,
  refetchTriggerToggle,
  isCollectedSettings,
  currentOptimizationPriorityLevels = [],
  selectedSources = DEFAULT_SOURCES,
  sourceFilterEnabled = isCollectedSettings,
}: DigitalTwinSettingsCalendarProps): ReactElement {
  const { t } = useTranslation()

  const startTimeProductionPlan = Datetime.getStartOfDay(Datetime.getISONow(0))
  const endTimeProductionPlan = Datetime.getEndOfDay(Datetime.getISONow(168))
  const previousWeekReferenceTime = Datetime.subtractWeeks(Datetime.getISONow(0), 1)
  const { startTime: startTimePreviousWeek, endTime: endTimePreviousWeek } = Datetime.getStartAndEndOfTimePeriod(
    previousWeekReferenceTime,
    'week'
  )

  const initialStartTime = isCollectedSettings ? startTimePreviousWeek : startTimeProductionPlan
  const initialEndTime = isCollectedSettings ? endTimePreviousWeek : endTimeProductionPlan

  const [calendarZoom, setCalendarZoom] = useState<CalendarZoom>(isCollectedSettings ? 'week' : 'productionPlan')
  const [startTime, setStartTime] = useState<ISODateTime>(initialStartTime)
  const [endTime, setEndTime] = useState<ISODateTime>(initialEndTime)
  const [selectedCustomPeriod, setSelectedCustomPeriod] = useState<{ startTime: ISODateTime; endTime: ISODateTime }>({
    startTime: initialStartTime,
    endTime: initialEndTime,
  })

  const [filteredSettings, setFilteredSettings] = useState<DigitalTwinSetting[] | null>(null)
  const { mutateAsync: fetchSettings, isPending: isPendingSettings } = useDigitalTwinSettings()

  const refetchSettings = useCallback(() => {
    if (filteredProperties && filteredProperties.length > 0) {
      fetchSettings({
        digitalTwinUid: digitalTwin?.uid,
        properties: filteredProperties,
        priorities: settingPriorityLevels ?? [],
        activeFrom: startTime,
        activeTo: endTime,
        usePropertyFiltersList: settingPropertyFilterEnabled,
      }).then(setFilteredSettings)
    }
  }, [
    settingPriorityLevels,
    digitalTwin?.uid,
    fetchSettings,
    filteredProperties,
    startTime,
    endTime,
    settingPropertyFilterEnabled,
  ])
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchTriggerToggle is a deliberate refetch trigger after edits
  useEffect(refetchSettings, [refetchSettings, refetchTriggerToggle])

  useEffect(() => {
    setSelectedCustomPeriod({ startTime, endTime })
  }, [startTime, endTime])

  useEffect(() => {
    onPeriodChange?.(startTime, endTime)
  }, [startTime, endTime, onPeriodChange])

  if (!filteredSettings) {
    return <LoadingPlaceholderContainer height={200} />
  }

  const sourceFilteredSettings = filterBySource(filteredSettings, sourceFilterEnabled, selectedSources)

  const { categories, events } = generateDTSCCategoriesAndEvents(
    digitalTwin,
    configurableProperties,
    sourceFilteredSettings,
    activeBaseSettings,
    settingPriorityLevels,
    t,
    isCollectedSettings,
    currentOptimizationPriorityLevels
  )
  return (
    <div className={styles.DigitalTwinSettingsCalendar}>
      <>
        <Inline spaceBetween>
          <div className={styles.DigitalTwinSettingsCalendar_HeaderControls}>
            {(calendarZoom === 'month' || calendarZoom === 'week') && (
              <div className={styles.DigitalTwinSettingsCalendar_TimePeriodPicker}>
                <TimePeriodPicker
                  periodType={calendarZoom === 'month' ? 'month' : 'week'}
                  startTime={startTime}
                  setTimePeriod={(date, periodType, timePeriodLength) => {
                    if (date && periodType && timePeriodLength) {
                      const { startTime, endTime } = Datetime.getStartAndEndOfTimePeriod(date, periodType)
                      setStartTime(startTime)
                      setEndTime(endTime)
                    }
                  }}
                />
              </div>
            )}
            {isCollectedSettings && calendarZoom === 'custom' && (
              <div className={styles.DigitalTwinSettingsCalendar_CustomPeriodPicker}>
                <DatePickerPeriod
                  startDate={selectedCustomPeriod.startTime}
                  endDate={selectedCustomPeriod.endTime}
                  onSelect={(customStartTime, customEndTime) => {
                    if (!customStartTime || !customEndTime) {
                      return
                    }

                    setSelectedCustomPeriod({
                      startTime: customStartTime,
                      endTime: customEndTime,
                    })
                  }}
                />
                <Button
                  primary
                  onClick={() => {
                    setStartTime(selectedCustomPeriod.startTime)
                    setEndTime(selectedCustomPeriod.endTime)
                  }}
                >
                  {t('Load period')}
                </Button>
              </div>
            )}
            <Switch
              value={calendarZoom}
              onClick={(value) => {
                if (value === 'productionPlan') {
                  setStartTime(startTimeProductionPlan)
                  setEndTime(endTimeProductionPlan)
                } else if (value === 'month') {
                  const timeToStartFrom = Datetime.getStartTimeForLatestMonth(startTime, endTime)

                  const currentYear = new Date(Datetime.toLocalTime(timeToStartFrom)).getFullYear()
                  const currentMonth = new Date(Datetime.toLocalTime(timeToStartFrom)).getMonth() + 1
                  const { startTime: newStartTime, endTime: newEndTime } = Datetime.getStartTimeEndTimeOfMonth(
                    currentYear,
                    currentMonth as Month
                  )
                  setStartTime(newStartTime)
                  setEndTime(newEndTime)
                } else if (value === 'week') {
                  const timeToStartFrom = isCollectedSettings
                    ? Datetime.subtractWeeks(Datetime.getISONow(0), 1)
                    : Datetime.getStartTimeForLatestMonth(startTime, endTime)
                  const { startTime: newStartTime, endTime: newEndTime } = Datetime.getStartAndEndOfTimePeriod(
                    timeToStartFrom,
                    'week'
                  )

                  setStartTime(newStartTime)
                  setEndTime(newEndTime)
                } else if (value === 'custom') {
                  setSelectedCustomPeriod({ startTime, endTime })
                }

                setCalendarZoom(value as CalendarZoom)
              }}
              items={[
                ...(isCollectedSettings
                  ? [
                      {
                        value: `custom`,
                        label: t(`Custom`),
                      },
                    ]
                  : []),
                ...(!isCollectedSettings
                  ? [
                      {
                        value: `productionPlan`,
                        label: t(`Production plan period`),
                      },
                    ]
                  : []),
                {
                  value: `week`,
                  label: isCollectedSettings ? t(`Previous week`) : t(`Week`),
                },
                {
                  value: `month`,
                  label: t(`Month`),
                },
              ]}
            />
          </div>
        </Inline>

        {/* Stays mounted while it refetches, after a save or a new period, so it keeps its scroll position.
            Its settings are about to be replaced meanwhile, so they can't be opened, and a spinner says so. */}
        <div aria-busy={isPendingSettings} className={styles.DigitalTwinSettingsCalendar_Body}>
          <div className={isPendingSettings ? styles.DigitalTwinSettingsCalendar_Refreshing : undefined}>
            <BarCalendar
              startTime={startTime}
              endTime={endTime}
              events={events}
              categories={categories}
              isEventHighlighted={isEventHighlighted}
              onClick={
                isPendingSettings
                  ? undefined
                  : (event) => {
                      const setting = sourceFilteredSettings.find((setting) => isBarOfSetting(event, setting))
                      onEditDeviationSetting(setting)
                    }
              }
              header={t('Asset')}
              categoryColumnWidth={180}
              isCollectedSettings={isCollectedSettings}
            />
          </div>
          {isPendingSettings && (
            <div className={styles.DigitalTwinSettingsCalendar_Spinner}>
              <CircularProgress aria-label={t('Loading...')} />
            </div>
          )}
        </div>
      </>
    </div>
  )
}
